import React, { useState, useEffect, useRef } from "react";
import RemainingDaysBadge from "./common/RemainingDaysBadge";
import { patchAdExtensionAPI } from "../../../../../api/index";
import { useAuth } from "../../../../../hooks/useAuth";
import {
  convertUTCToKoreaTime,
  convertKoreaTimeToUTC,
} from "../../../../../utils/helpers";
import { isSpecialUser } from "../../../../../utils/environment";
import "./AdExtensionModal.css";

// 연장기간 선택 옵션 (일)
const EXTENSION_PERIOD_OPTIONS = [
  7, 10, 14, 20, 21, 28, 30, 35, 40, 42, 49, 50, 56, 60, 63, 70, 77, 80, 84,
  90,
];

const AdExtensionModal = ({
  isOpen,
  onClose,
  selectedAds, // 선택된 광고들 (단일 또는 다수)
  onExtend,
}) => {
  const [extensionPeriods, setExtensionPeriods] = useState({}); // 각 광고별 연장기간
  const [isProcessing, setIsProcessing] = useState(false); // 연장 처리 중 상태
  const [isUserInfoUpdated, setIsUserInfoUpdated] = useState(false); // 사용자 정보 업데이트 완료 여부
  const [stablePriceData, setStablePriceData] = useState(null); // 안정적인 단가 데이터
  const { user: authUser, updateUserInfo } = useAuth();
  const isSpecial = isSpecialUser(authUser?.id);

  // useAuth에서 직접 가져온 authUser 사용
  const getUnitPrice = (ad) => {
    // 안정적인 단가 데이터가 있으면 사용
    if (stablePriceData) {
      const missionTemplate = ad.missionTemplate;
      const priceDetail = stablePriceData.find(
        (item) => item.mission_template_id === missionTemplate
      );
      return priceDetail?.price || 0;
    }

    // fallback: authUser에서 가져오기
    if (!authUser?.price_grade_detail?.data) {
      return 0;
    }

    const missionTemplate = ad.missionTemplate;
    const priceDetail = authUser.price_grade_detail.data.find(
      (item) => item.mission_template_id === missionTemplate
    );

    return priceDetail?.price || 0;
  };

  const depositAmount = authUser?.deposit || 0; //depositAmount이 가장 최신의 deposit

  // 모달이 열렸을 때 사용자 정보 최신화 (한 번만)
  useEffect(() => {
    if (isOpen && !isUserInfoUpdated) {
      const updateInfo = async () => {
        try {
          const result = await updateUserInfo();
          if (result.success && result.userInfo?.price_grade_detail?.data) {
            // 단가 데이터를 안정적으로 저장
            setStablePriceData(result.userInfo.price_grade_detail.data);
          }
        } catch (error) {
          console.error("❌ 사용자 정보 업데이트 실패:", error);
        }
      };

      updateInfo();
      setIsUserInfoUpdated(true);
    }
  }, [isOpen, updateUserInfo, isUserInfoUpdated]);

  // 모달이 닫힐 때 상태 초기화
  useEffect(() => {
    if (!isOpen) {
      setIsUserInfoUpdated(false);
      setStablePriceData(null);
    }
  }, [isOpen]);

  // 초기 연장기간 설정 (specialUser: 1일, 그 외: 7일)
  useEffect(() => {
    if (selectedAds && selectedAds.length > 0) {
      const initialPeriods = {};
      const defaultPeriod = isSpecial ? 1 : 7;
      selectedAds.forEach((ad) => {
        initialPeriods[ad.ad_id || ad.id] = defaultPeriod;
      });
      setExtensionPeriods(initialPeriods);
    }
  }, [selectedAds, isSpecial]);

  const getMaxPeriod = () =>
    isSpecial ? 365 : EXTENSION_PERIOD_OPTIONS[EXTENSION_PERIOD_OPTIONS.length - 1];

  const clampPeriod = (period, fallback) => {
    const value = parseInt(period, 10);
    const base = isNaN(value) ? fallback : value;
    return Math.max(1, Math.min(getMaxPeriod(), base));
  };

  // 연장기간 변경 핸들러 (specialUser: 1~365 입력, 그 외: 1~옵션 최대일)
  const handlePeriodChange = (adId, period) => {
    const value = parseInt(period, 10);
    const allowed = isSpecial
      ? clampPeriod(value, 7)
      : EXTENSION_PERIOD_OPTIONS.includes(value) ||
          (value >= 1 && value <= getMaxPeriod())
        ? clampPeriod(value, 7)
        : 7;
    setExtensionPeriods((prev) => ({
      ...prev,
      [adId]: allowed,
    }));
  };

  // 선택 광고 전체 연장기간을 1일씩 조정 (1일 미만 불가)
  const adjustAllPeriods = (delta) => {
    if (!selectedAds?.length) return;
    const fallback = isSpecial ? 1 : 7;
    setExtensionPeriods((prev) => {
      const next = { ...prev };
      selectedAds.forEach((ad) => {
        const adId = ad.ad_id || ad.id;
        const current = next[adId] ?? fallback;
        next[adId] = clampPeriod(current + delta, fallback);
      });
      return next;
    });
  };

  const adjustAllPeriodsRef = useRef(adjustAllPeriods);
  adjustAllPeriodsRef.current = adjustAllPeriods;
  const holdDelayRef = useRef(null);
  const holdIntervalRef = useRef(null);

  const stopPeriodHold = () => {
    if (holdDelayRef.current) {
      clearTimeout(holdDelayRef.current);
      holdDelayRef.current = null;
    }
    if (holdIntervalRef.current) {
      clearInterval(holdIntervalRef.current);
      holdIntervalRef.current = null;
    }
  };

  // 버튼을 누르고 있으면 반복 조정, 떼면 멈춤
  const startPeriodHold = (delta) => {
    if (isProcessing) return;
    stopPeriodHold();
    adjustAllPeriodsRef.current(delta);
    holdDelayRef.current = setTimeout(() => {
      holdIntervalRef.current = setInterval(() => {
        adjustAllPeriodsRef.current(delta);
      }, 80);
    }, 350);
  };

  useEffect(() => stopPeriodHold, []);

  const getPeriodOptions = (current) => {
    const value = parseInt(current, 10);
    if (isNaN(value) || EXTENSION_PERIOD_OPTIONS.includes(value)) {
      return EXTENSION_PERIOD_OPTIONS;
    }
    return [...EXTENSION_PERIOD_OPTIONS, value].sort((a, b) => a - b);
  };

  // 연장수량 계산 (연장기간 × 일일수량)
  const calculateExtensionQuantity = (ad) => {
    const period = extensionPeriods[ad.ad_id || ad.id] || 7;
    const dailyQuantity = ad.dailyQuantity || ad.totalQuantity || 0;
    return period * dailyQuantity;
  };

  // 마감예정일 계산 (현재 종료일 + 연장기간) - 한국 시간 기준으로 계산 후 UTC로 변환
  const calculateNewEndDate = (ad) => {
    const period = extensionPeriods[ad.ad_id || ad.id] || 7;

    // 1. 현재 종료일을 한국 시간으로 변환
    const koreaEndDate = convertUTCToKoreaTime(ad.endDate);
    if (!koreaEndDate) {
      console.error("종료일 변환 실패:", ad.endDate);
      return ad.endDate; // 원본 반환
    }

    // 2. 한국 시간 기준으로 period일 추가하고 23:59:59로 설정
    const newKoreaEndDate = new Date(
      koreaEndDate.getTime() + period * 24 * 60 * 60 * 1000
    );

    // 한국 시간 기준으로 23:59:59로 설정 (하루 종일 광고)
    newKoreaEndDate.setHours(23, 59, 59, 999);

    // 3. 한국 시간을 UTC로 변환하여 서버에 전송
    const utcEndDate = convertKoreaTimeToUTC(newKoreaEndDate);
    if (!utcEndDate) {
      console.error("UTC 변환 실패:", newKoreaEndDate);
      return ad.endDate; // 원본 반환
    }

    return utcEndDate.toISOString();
  };

  // 결제금액 계산 (1회단가 × 연장수량)
  const calculatePaymentAmount = (ad) => {
    const unitPrice = getUnitPrice(ad);

    if (unitPrice === 0) {
      console.error(
        `광고 ID ${ad.ad_id || ad.id}의 1회 단가를 가져올 수 없습니다.`
      );
      return 0;
    }

    const quantity = calculateExtensionQuantity(ad);
    return unitPrice * quantity;
  };

  // 총 결제금액 계산
  const calculateTotalAmount = () => {
    return selectedAds.reduce((total, ad) => {
      return total + calculatePaymentAmount(ad);
    }, 0);
  };

  // 연장 실행 - 일괄 연장 로직
  const handleExtend = async () => {
    try {
      setIsProcessing(true);

      // 연장할 광고가 1개인지 2개 이상인지 확인
      const totalAds = selectedAds.length;

      // 현재 예치금 (연장 처리 중에 업데이트됨)
      let currentDeposit = depositAmount;

      // 순차적으로 연장 처리 (for...of 사용)
      for (let i = 0; i < selectedAds.length; i++) {
        const ad = selectedAds[i];
        const adId = ad.ad_id || ad.id;
        const currentIndex = i + 1;

        // 현재 광고의 연장 데이터 생성
        const extensionData = {
          ad_id: adId,
          totalQuantity:
            (ad.totalQuantity || 0) + calculateExtensionQuantity(ad),
          endDate: calculateNewEndDate(ad),
          updateDeposit: {
            deposit: currentDeposit - calculatePaymentAmount(ad),
            price: (ad.price || 0) + calculatePaymentAmount(ad),
            logs: {
              balance: currentDeposit - calculatePaymentAmount(ad), // 연장 후 예치금
              amount: -calculatePaymentAmount(ad),
              admin_id: 0,
              title: `'${ad.missionName}' ${
                extensionPeriods[adId] || 1
              }일 광고 연장`,
            },
          },
        };

        // API 호출 (순차적으로)
        try {
          await patchAdExtensionAPI(extensionData);

          // 다음 광고 연장을 위해 예치금 업데이트
          currentDeposit -= calculatePaymentAmount(ad);
        } catch (error) {
          console.error(`❌ ${adId}번 광고 연장 실패:`, error);
          throw new Error(
            `${ad.missionName} 광고 연장에 실패했습니다: ${
              error.response?.data?.msg || error.message
            }`
          );
        }
      }

      // 모든 광고 연장 완료 후 예치금 최신화
      try {
        await updateUserInfo();
      } catch (error) {
        console.error("❌ 예치금 업데이트 실패:", error);
        // 예치금 업데이트 실패해도 연장은 성공했으므로 계속 진행
      }

      onExtend("success");
      onClose();
    } catch (error) {
      console.error("❌ 광고 연장 중 오류 발생:", error);
      alert(`광고 연장에 실패했습니다: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="adextensionmodal-overlay"
      onClick={(e) => {
        // 오버레이(배경)를 직접 클릭했을 때만 모달 닫기
        if (e.target === e.currentTarget && !isProcessing) {
          onClose();
        }
      }}
    >
      <div
        className="adextensionmodal-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div className="adextensionmodal-header">
          <h2>광고 연장하기</h2>
          <button
            className="adextensionmodal-close-btn"
            onClick={onClose}
          ></button>
        </div>

        {/* 예치금 표시 */}
        <div className="adextensionmodal-deposit-info">
          <span className="adextensionmodal-deposit-label">예치금:</span>
          <span className="adextensionmodal-deposit-amount">
            {depositAmount.toLocaleString()} 원
          </span>
        </div>

        {/* 광고 목록 테이블 */}
        <div className="adextensionmodal-ads-table-container">
          <table className="adextensionmodal-ads-table">
            <thead>
              <tr>
                <th>광고명</th>
                <th>잔여기간</th>
                <th>
                  <div className="adextensionmodal-period-header">
                    <span>연장기간</span>
                    <div className="adextensionmodal-period-bulk-group">
                      <button
                        type="button"
                        className="adextensionmodal-period-bulk-btn adextensionmodal-period-bulk-btn--plus"
                        onPointerDown={(e) => {
                          if (e.button !== 0) return;
                          e.preventDefault();
                          e.currentTarget.setPointerCapture(e.pointerId);
                          startPeriodHold(1);
                        }}
                        onPointerUp={stopPeriodHold}
                        onPointerCancel={stopPeriodHold}
                        onKeyDown={(e) => {
                          if (e.repeat) return;
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            startPeriodHold(1);
                          }
                        }}
                        onKeyUp={(e) => {
                          if (e.key === "Enter" || e.key === " ") stopPeriodHold();
                        }}
                        disabled={isProcessing}
                      >
                        일괄 +1
                      </button>
                      <button
                        type="button"
                        className="adextensionmodal-period-bulk-btn adextensionmodal-period-bulk-btn--minus"
                        onPointerDown={(e) => {
                          if (e.button !== 0) return;
                          e.preventDefault();
                          e.currentTarget.setPointerCapture(e.pointerId);
                          startPeriodHold(-1);
                        }}
                        onPointerUp={stopPeriodHold}
                        onPointerCancel={stopPeriodHold}
                        onKeyDown={(e) => {
                          if (e.repeat) return;
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            startPeriodHold(-1);
                          }
                        }}
                        onKeyUp={(e) => {
                          if (e.key === "Enter" || e.key === " ") stopPeriodHold();
                        }}
                        disabled={isProcessing}
                      >
                        일괄 −1
                      </button>
                    </div>
                  </div>
                </th>
                <th>1회 단가</th>
                <th>일일수량</th>
                <th>연장수량</th>
                <th>마감 예정일</th>
                <th>결제 금액</th>
              </tr>
            </thead>
            <tbody>
              {selectedAds.map((ad) => (
                <tr key={ad.ad_id || ad.id}>
                  <td className="adextensionmodal-extension-ad-name-cell">
                    <div className="adextensionmodal-ad-info">
                      {/* 1-1. imageurl */}
                      <img
                        src={ad.imageUrl || "/logo192.png"}
                        alt="광고 이미지"
                        className="adextensionmodal-ad-image"
                        onError={(e) => {
                          e.target.src = "/logo192.png";
                        }}
                      />
                      {/* 1-2. column (storename, missionname) */}
                      <div className="adextensionmodal-ad-details">
                        {/* 1-2-1. storename (null이면 표시하지 않음) */}
                        {ad.storeName && (
                          <div className="adextensionmodal-store-name">
                            {ad.storeName}
                          </div>
                        )}
                        {/* 1-2-2. missionname */}
                        <div className="adextensionmodal-mission-name">
                          {ad.missionName}
                        </div>
                      </div>
                    </div>
                  </td>
                  {/* 1-3. 잔여기간 */}
                  <td className="adextensionmodal-remaining-days-cell">
                    <RemainingDaysBadge
                      startDate={ad.startDate}
                      endDate={ad.endDate}
                      status={ad.status}
                    />
                  </td>
                  <td>
                    {isSpecial ? (
                      <>
                        <input
                          type="number"
                          min={1}
                          max={365}
                          value={extensionPeriods[ad.ad_id || ad.id] ?? 7}
                          onChange={(e) =>
                            handlePeriodChange(
                              ad.ad_id || ad.id,
                              e.target.value === ""
                                ? 7
                                : parseInt(e.target.value, 10)
                            )
                          }
                          className="adextensionmodal-period-input"
                          disabled={isProcessing}
                        />
                        <span className="adextensionmodal-period-unit">일</span>
                      </>
                    ) : (
                      <select
                        value={extensionPeriods[ad.ad_id || ad.id] ?? 7}
                        onChange={(e) =>
                          handlePeriodChange(
                            ad.ad_id || ad.id,
                            parseInt(e.target.value, 10)
                          )
                        }
                        className="adextensionmodal-period-select"
                        disabled={isProcessing}
                      >
                        {getPeriodOptions(
                          extensionPeriods[ad.ad_id || ad.id] ?? 7
                        ).map((days) => (
                          <option key={days} value={days}>
                            {days}일
                          </option>
                        ))}
                      </select>
                    )}
                  </td>
                  <td>
                    <span className="adextensionmodal-unit-price">
                      {(() => {
                        const unitPrice = getUnitPrice(ad);
                        if (unitPrice > 0) {
                          return `${unitPrice}원`;
                        } else {
                          return "단가 없음";
                        }
                      })()}
                    </span>
                  </td>
                  <td>
                    <span className="adextensionmodal-daily-quantity">
                      {ad.dailyQuantity || ad.totalQuantity || 0}회
                    </span>
                  </td>
                  <td>
                    <span className="adextensionmodal-extension-quantity">
                      {calculateExtensionQuantity(ad).toLocaleString()}회
                    </span>
                  </td>
                  <td>
                    <span className="adextensionmodal-new-end-date">
                      {(() => {
                        const endDate = calculateNewEndDate(ad);
                        // 년월일만 표시
                        return endDate.split("T")[0].replace(/-/g, ".");
                      })()}
                    </span>
                  </td>
                  <td>
                    <span className="adextensionmodal-payment-amount">
                      {calculatePaymentAmount(ad).toLocaleString()}원
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 하단 합계 및 버튼 */}
        <div className="adextensionmodal-footer">
          <div className="adextensionmodal-total-section">
            <span className="adextensionmodal-total-label">합계:</span>
            <span className="adextensionmodal-total-amount">
              {calculateTotalAmount().toLocaleString()}원
            </span>
          </div>
          <div className="adextensionmodal-action-buttons">
            <button
              className="adextensionmodal-close-button"
              onClick={onClose}
              disabled={isProcessing}
            >
              닫기
            </button>
            <button
              className="adextensionmodal-extend-button"
              onClick={handleExtend}
              disabled={isProcessing || calculateTotalAmount() > depositAmount}
            >
              {isProcessing ? "연장 중..." : "연장"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdExtensionModal;
